/**
 * ReviewsSection.js — Recensioni clienti sulla home del sito pubblico.
 *
 * Usa usePublicReviews(companyId), hook già esistente nel progetto (letto
 * da reviews/{companyId} filtrando status === 'approved') ma finora mai
 * collegato a nessuna pagina pubblica: le recensioni raccolte dall'area
 * cliente non venivano mai mostrate ai visitatori del sito.
 *
 * Non mostra nulla se non ci sono ancora recensioni approvate, quindi un
 * noleggio nuovo non ha una sezione vuota o imbarazzante.
 */

import React from 'react';
import { Quote } from 'lucide-react';
import { usePublicReviews } from '../../hooks/useReviews';
import ReviewStars from '../ReviewStars';

const ReviewsSection = ({ companyId, accentColor = '#2563EB' }) => {
  const { reviews, loading, avgRating, count } = usePublicReviews(companyId, true);

  if (loading || count === 0) return null;

  // Mostriamo al massimo le 6 recensioni più recenti: la home resta leggera
  // anche per aziende con uno storico lungo.
  const shown = reviews.slice(0, 6);

  return (
    <div className="bg-slate-50 border-y border-slate-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        <div className="text-center mb-10">
          <h2 className="text-2xl sm:text-3xl font-display font-extrabold text-slate-900 tracking-tight mb-2">
            Cosa dicono i nostri clienti
          </h2>
          <div className="flex items-center justify-center gap-2">
            <ReviewStars rating={Number(avgRating)} size="md" />
            <span className="text-sm font-bold text-slate-700">{avgRating}</span>
            <span className="text-sm text-slate-400">· {count} {count === 1 ? 'recensione' : 'recensioni'}</span>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {shown.map((review) => (
            <div key={review.id} className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 flex flex-col">
              <Quote className="w-6 h-6 mb-3 opacity-20" style={{ color: accentColor }} />
              <ReviewStars rating={review.rating} size="sm" />
              {review.titolo && (
                <p className="font-bold text-slate-900 text-sm mt-3">{review.titolo}</p>
              )}
              {review.testo && (
                <p className="text-sm text-slate-600 mt-1.5 leading-relaxed line-clamp-4 flex-1">{review.testo}</p>
              )}
              <p className="text-xs font-semibold text-slate-400 mt-4">
                {review.userName || 'Cliente'}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ReviewsSection;
